import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/waitlist`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const MESSAGES = {
  duplicate: "You're already on the list with this email.",
  rate: "Too many attempts. Please wait a minute and try again.",
  error: "Something went wrong on our side. Please try again.",
};

const Success = ({ done, onReset }) => (
  <div className="py-2 text-center" data-testid="waitlist-success">
    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#7866E8] to-[#80D9EB] text-white"><Check size={24} aria-hidden /></span>
    <p className="mt-4 text-[24px] font-bold tracking-[-0.03em] text-[#08090D]" data-testid="waitlist-success-title">
      {done.name ? `You're on the list, ${done.name}.` : "You're on the list."}
    </p>
    <p className="mt-2 text-[15px] text-[#3c4052]">We'll write to <span className="font-semibold" data-testid="waitlist-success-email">{done.email}</span> when early access opens.</p>
    <button type="button" onClick={onReset} data-testid="waitlist-reset-button" className="mt-5 text-[14px] font-semibold text-[#7866E8] hover:underline">Add another email</button>
  </div>
);

export const WaitlistForm = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [status, setStatus] = useState("idle");
  const [done, setDone] = useState(null);

  const inFlight = useRef(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (inFlight.current || status === "success") return;
    const clean = email.trim();
    if (!EMAIL_RE.test(clean)) {
      setFieldError("Please enter a valid email address.");
      return;
    }
    setFieldError("");
    setStatus("loading");
    inFlight.current = true;
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: clean, name: name.trim() || null, source: "landing" }),
      });
      if (res.status === 201) {
        setDone({ name: name.trim(), email: clean.toLowerCase() });
        setStatus("success");
      } else if (res.status === 409) setStatus("duplicate");
      else if (res.status === 422) {
        setStatus("idle");
        setFieldError("Please enter a valid email address.");
      } else if (res.status === 429) setStatus("rate");
      else setStatus("error");
    } catch {
      setStatus("error");
    } finally {
      inFlight.current = false;
    }
  };

  const reset = () => {
    setEmail("");
    setName("");
    setDone(null);
    setStatus("idle");
  };

  const live =
    status === "success" ? `You're on the list${done?.name ? `, ${done.name}` : ""}. Confirmation for ${done?.email}.` : status === "loading" ? "Joining the waitlist…" : MESSAGES[status] || fieldError;
  const liveRegion = <p className="sr-only" aria-live="polite" role="status" data-testid="waitlist-live">{live}</p>;

  if (status === "success") return <>{liveRegion}<Success done={done} onReset={reset} /></>;

  return (
    <>
    {liveRegion}
    <form onSubmit={submit} noValidate className="text-left" data-testid="waitlist-form">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <div>
          <label htmlFor="waitlist-name" className="mb-1.5 block text-[13px] font-semibold text-[#2f3242]">Name <span className="font-normal text-[#777B8D]">(optional)</span></label>
          <input id="waitlist-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} autoComplete="given-name" placeholder="Karna" data-testid="waitlist-name-input" className="waitlist-input" />
        </div>
        <div>
          <label htmlFor="waitlist-email" className="mb-1.5 block text-[13px] font-semibold text-[#2f3242]">Email</label>
          <input
            id="waitlist-email"
            type="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (fieldError) setFieldError("");
              if (status !== "loading") setStatus("idle");
            }}
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            aria-invalid={!!fieldError}
            aria-describedby={fieldError ? "waitlist-email-error" : undefined}
            data-testid="waitlist-email-input"
            className={`waitlist-input ${fieldError ? "is-invalid" : ""}`}
          />
        </div>
      </div>
      {fieldError && <p id="waitlist-email-error" className="mt-2 text-[13px] font-medium text-[#5a3fd6]" data-testid="waitlist-email-error">{fieldError}</p>}
      <button type="submit" disabled={status === "loading"} data-testid="waitlist-submit-button" className="btn-primary mt-4 h-14 w-full text-[16px] disabled:cursor-wait disabled:opacity-70">
        {status === "loading" ? <><Loader2 size={18} className="animate-spin" aria-hidden /> Joining…</> : "Join the Waitlist"}
      </button>
      {MESSAGES[status] && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl bg-white/70 px-4 py-3" data-testid={`waitlist-${status}-message`}>
          <p className="text-[14px] font-medium text-[#08090D]">{MESSAGES[status]}</p>
          {status === "error" && (
            <button type="button" onClick={submit} data-testid="waitlist-retry-button" className="flex shrink-0 items-center gap-1 text-[13px] font-semibold text-[#7866E8]"><RotateCcw size={13} aria-hidden /> Retry</button>
          )}
        </div>
      )}
      <p className="mt-4 text-[12.5px] leading-relaxed text-[#4c5064]">
        By joining, you agree to receive early-access updates from Social AI. We'll never share your email. See our{" "}
        <Link to="/privacy" className="font-semibold text-[#08090D] underline underline-offset-2" data-testid="waitlist-privacy-link">Privacy Policy</Link>.
      </p>
    </form>
    </>
  );
};
